import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Merge, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Modal para revisar y fusionar SELECTIVAMENTE los laboratorios duplicados.
 * El admin marca por grupo cuál quiere fusionar y confirma la variante canónica.
 */
export default function LabDuplicatesModal({ onClose, onMerged }) {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({}); // {normalized_key: {selected: bool, canonical: variant}}
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    api.get("/admin/lab-duplicates")
      .then((r) => {
        setGroups(r.data.groups || []);
        // Ninguno preseleccionado — el admin decide
        const init = {};
        (r.data.groups || []).forEach((g) => {
          init[g.normalized] = { selected: false, canonical: g.canonical };
        });
        setSelected(init);
      })
      .finally(() => setLoading(false));
  }, []);

  const toggleGroup = (key) => setSelected((s) => ({ ...s, [key]: { ...s[key], selected: !s[key].selected } }));
  const setCanonical = (key, name) => setSelected((s) => ({ ...s, [key]: { ...s[key], canonical: name } }));

  const totalSelected = Object.values(selected).filter((s) => s.selected).length;

  const runMerge = async () => {
    setMerging(true);
    let ok = 0;
    let fail = 0;
    for (const g of groups) {
      const state = selected[g.normalized];
      if (!state?.selected) continue;
      const others = g.variants.filter((v) => v.name !== state.canonical).map((v) => v.name);
      if (!others.length) continue;
      try {
        await api.post("/admin/lab-duplicates/merge", { canonical: state.canonical, variants: others });
        ok += 1;
      } catch (e) {
        fail += 1;
      }
    }
    setMerging(false);
    if (ok > 0) toast.success(`${ok} grupo(s) fusionados`);
    if (fail > 0) toast.error(`${fail} fallo(s)`);
    onMerged?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-6 overflow-y-auto" onClick={onClose} data-testid="lab-duplicates-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-bold mb-0.5">Limpieza · laboratorios duplicados</div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">
              Unificar laboratorios
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              {loading ? "Escaneando…" : `${groups.length} grupo${groups.length !== 1 ? "s" : ""} detectado${groups.length !== 1 ? "s" : ""}`}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-slate-100 text-slate-500" data-testid="close-lab-dup">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-slate-500 inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="h-4 w-4 animate-spin" /> Buscando duplicados en catálogo, convenios y preferencias…
            </div>
          )}
          {!loading && groups.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
              <div className="font-bold text-lg">Todo limpio ✓</div>
              <div className="text-sm mt-1">No hay laboratorios duplicados en la base de datos.</div>
            </div>
          )}
          {!loading && groups.length > 0 && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
                <div className="inline-flex items-center gap-1.5 font-bold"><AlertTriangle className="h-3.5 w-3.5" /> Aviso</div>
                <p className="mt-1 text-xs">
                  Marca solo los grupos que <strong>tú</strong> quieras unificar. Puedes cambiar la variante canónica (el nombre final que quedará) haciendo click en su nombre.
                  Los otros nombres se renombrarán al canónico en <code className="bg-white px-1 rounded">catalog</code>, <code className="bg-white px-1 rounded">supplier_agreements</code> (fusionando vademécum + convenio) y <code className="bg-white px-1 rounded">molecule_preferences</code>.
                </p>
              </div>

              <div className="space-y-3">
                {groups.map((g) => {
                  const state = selected[g.normalized] || {};
                  return (
                    <div key={g.normalized} className={`border rounded-lg overflow-hidden ${state.selected ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200"}`}>
                      <div className="px-4 py-2.5 bg-slate-50 flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={!!state.selected}
                          onChange={() => toggleGroup(g.normalized)}
                          className="w-4 h-4 accent-amber-600"
                          data-testid={`toggle-group-${g.normalized}`}
                        />
                        <div className="flex-1 font-bold text-slate-900 text-sm">
                          {g.variants.length} variantes · {g.variants.reduce((s, v) => s + v.total, 0)} referencias afectadas
                        </div>
                        <div className="text-xs text-slate-500">
                          Canónico: <strong className="text-amber-800">{state.canonical}</strong>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {g.variants.map((v) => (
                          <button
                            key={v.name}
                            onClick={() => setCanonical(g.normalized, v.name)}
                            disabled={!state.selected}
                            className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between disabled:opacity-60 disabled:cursor-not-allowed ${state.canonical === v.name && state.selected ? "bg-amber-50" : "hover:bg-slate-50"}`}
                            data-testid={`pick-canonical-${v.name}`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {state.canonical === v.name ? (
                                <span className="text-amber-600 font-bold text-xs">★</span>
                              ) : (
                                <span className="text-slate-300 text-xs">☆</span>
                              )}
                              <span className={state.canonical === v.name ? "font-semibold text-slate-900" : "text-slate-700"}>{v.name}</span>
                            </div>
                            <div className="text-xs text-slate-500 font-mono-num ml-2 whitespace-nowrap">
                              cat: <strong>{v.catalog}</strong> · conv: <strong>{v.agreements}</strong> · pref: <strong>{v.prefs}</strong>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            <strong>{totalSelected}</strong> grupo{totalSelected !== 1 ? "s" : ""} seleccionado{totalSelected !== 1 ? "s" : ""}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancelar</button>
            <button
              onClick={runMerge}
              disabled={totalSelected === 0 || merging}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white font-bold px-5 py-2 rounded-full text-sm"
              data-testid="btn-merge-selected"
            >
              {merging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />}
              Fusionar seleccionados
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
